import type { KokoAnimationStateName } from "./animations";
import type { BirdRenderer } from "./SpriteRenderer";
import {
  getBirdStateAltIds,
  resolveBirdStateAsset,
  type BirdVariant,
} from "./birdVariants";

const PERCHED_BASE_LOOPS_PER_ALT = 3;

interface VideoRecord {
  readonly stateName: KokoAnimationStateName;
  readonly altId: string | null;
  readonly element: HTMLVideoElement;
  lastPlaybackTime: number;
  ready: boolean;
}

/** Video-backed bird renderer with per-state sprite fallback. */
export class VideoRenderer implements BirdRenderer {
  private readonly videos = new Map<string, VideoRecord>();
  private readonly failedStates = new Set<KokoAnimationStateName>();
  private currentState: KokoAnimationStateName = "flight";
  private currentAltId: string | null = null;
  private visibleVideo: HTMLVideoElement | null = null;
  private pendingVideo: HTMLVideoElement | null = null;
  private running = false;
  private destroyed = false;
  private perchedBaseLoopCount = 0;
  private perchedAltRotationIndex = 0;
  private autoPerchedAltId: string | null = null;
  private presentableReadyResolve: (() => void) | null = null;
  private presentableReadyPromise: Promise<void> = Promise.resolve();

  constructor(
    private readonly containerElement: HTMLElement,
    private readonly imageElement: HTMLImageElement,
    private readonly fallbackRenderer: BirdRenderer,
    private readonly baseUrl: string,
    private readonly variant: BirdVariant,
    scale = 1,
  ) {
    this.containerElement.style.position = "absolute";
    this.containerElement.style.inset = "0";
    this.containerElement.style.width = "100%";
    this.containerElement.style.height = "100%";
    this.containerElement.style.transformOrigin = "center bottom";
    this.containerElement.style.transform = `scale(${scale})`;
    this.imageElement.style.visibility = "visible";
    this.imageElement.style.opacity = "1";
    this.showState("flight", null);
  }

  setAnimation(
    stateName: KokoAnimationStateName,
    altId?: string | null,
  ): void {
    if (this.destroyed) return;
    const resolvedAltId = this.resolveAltId(stateName, altId ?? null);
    if (stateName === this.currentState && resolvedAltId === this.currentAltId) {
      return;
    }

    this.autoPerchedAltId = null;
    const shouldResetPerchedLoopCount =
      stateName !== "perched" ||
      resolvedAltId !== null ||
      this.currentState !== "perched" ||
      this.currentAltId !== null;
    if (shouldResetPerchedLoopCount) {
      this.perchedBaseLoopCount = 0;
    }

    this.currentState = stateName;
    this.currentAltId = resolvedAltId;
    this.showState(stateName, resolvedAltId);
  }

  start(): void {
    if (this.destroyed || this.running) return;
    this.running = true;
    if (this.visibleVideo) {
      void this.visibleVideo.play().catch(() => undefined);
      return;
    }
    if (this.pendingVideo) {
      void this.pendingVideo.play().catch(() => undefined);
      return;
    }
    this.fallbackRenderer.start();
  }

  stop(): void {
    this.running = false;
    this.visibleVideo?.pause();
    this.pendingVideo?.pause();
    this.fallbackRenderer.stop();
  }

  destroy(): void {
    this.destroyed = true;
    this.stop();
    this.visibleVideo = null;
    this.pendingVideo = null;
    this.currentAltId = null;
    // Remove only OUR video elements. The layer is shared across
    // renderer generations during a style swap — the successor may
    // already have mounted its own videos here, and a blanket
    // replaceChildren() would silently delete them (the bird then
    // "renders" into detached elements and vanishes).
    for (const record of this.videos.values()) {
      record.element.remove();
    }
    this.videos.clear();
    if (!this.containerElement.querySelector("video")) {
      this.containerElement.style.opacity = "0";
    }
  }

  private showState(
    stateName: KokoAnimationStateName,
    altId: string | null,
  ): void {
    const record = this.getVideo(stateName, altId);
    const previousVideo = this.visibleVideo;

    if (!record) {
      this.activateFallback(stateName, previousVideo);
      if (this.running) this.fallbackRenderer.start();
      else this.fallbackRenderer.stop();
      return;
    }

    record.element.currentTime = 0;
    record.lastPlaybackTime = 0;
    record.element.style.visibility = "hidden";
    record.element.style.opacity = "0";
    if (stateName === "perched" && altId === null) {
      for (const nextAltId of getBirdStateAltIds(this.variant, "perched")) {
        this.getVideo("perched", nextAltId);
      }
    }

    if (record.ready) {
      this.activateVideo(record, previousVideo);
    } else {
      this.pendingVideo = record.element;
      this.presentableReadyPromise = new Promise((resolve) => {
        this.presentableReadyResolve = resolve;
      });
      this.imageElement.style.visibility = "visible";
      this.imageElement.style.opacity = "1";
      this.containerElement.style.opacity = "0";
      if (this.running) void record.element.play().catch(() => undefined);
    }
  }

  private getVideo(
    stateName: KokoAnimationStateName,
    altId: string | null,
  ): VideoRecord | null {
    if (this.failedStates.has(stateName)) return null;
    const key = this.getVideoKey(stateName, altId);
    const existing = this.videos.get(key);
    if (existing) {
      if (!existing.element.isConnected) {
        // A sibling renderer's teardown (or any external DOM churn)
        // detached this element; styles applied to a detached video
        // render nothing. Re-mount before use.
        this.containerElement.appendChild(existing.element);
      }
      return existing;
    }

    const resolved = resolveBirdStateAsset(this.variant, stateName, altId);
    if (!resolved) return null;

    const element = document.createElement("video");
    element.dataset.kokoBirdState = stateName;
    if (altId !== null) {
      element.dataset.kokoBirdAlt = altId;
    }
    element.muted = true;
    element.defaultMuted = true;
    element.playsInline = true;
    element.loop = true;
    element.preload = "auto";
    element.poster = this.assetUrl(resolved.asset.poster);
    element.src = this.assetUrl(resolved.asset.webm);
    element.style.position = "absolute";
    element.style.inset = "0";
    element.style.width = "100%";
    element.style.height = "100%";
    element.style.objectFit = "contain";
    element.style.opacity = "0";
    element.style.visibility = "hidden";
    element.style.transition = "none";
    element.addEventListener("error", () => this.handleVideoError(stateName, element));
    element.addEventListener("loadeddata", () =>
      this.handleVideoReady(stateName, altId, element),
    );
    element.addEventListener("timeupdate", () =>
      this.handlePlaybackProgress(stateName, altId, element),
    );

    const record: VideoRecord = {
      stateName,
      altId,
      element,
      lastPlaybackTime: 0,
      ready: element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA,
    };
    this.videos.set(key, record);
    this.containerElement.appendChild(element);
    return record;
  }

  private handleVideoReady(
    stateName: KokoAnimationStateName,
    altId: string | null,
    element: HTMLVideoElement,
  ): void {
    if (this.destroyed) return;
    const key = this.getVideoKey(stateName, altId);
    const record = this.videos.get(key);
    if (!record) return;
    record.ready = true;
    if (this.pendingVideo === element) {
      if (this.currentState !== stateName || altId !== this.currentAltId) return;
      const previousVideo = this.visibleVideo;
      this.activateVideo(record, previousVideo);
      return;
    }
    if (this.visibleVideo === element) {
      this.resolvePresentable();
    }
  }

  private activateVideo(record: VideoRecord, previousVideo: HTMLVideoElement | null): void {
    this.fallbackRenderer.stop();
    this.visibleVideo = record.element;
    this.pendingVideo = null;
    record.element.style.visibility = "visible";
    record.element.style.opacity = "1";
    this.containerElement.style.opacity = "1";
    this.imageElement.style.visibility = "hidden";
    this.imageElement.style.opacity = "0";
    if (previousVideo && previousVideo !== record.element) {
      previousVideo.style.opacity = "0";
      previousVideo.style.visibility = "hidden";
      previousVideo.pause();
    }
    if (this.running) void record.element.play().catch(() => undefined);
    this.resolvePresentable();
  }

  private activateFallback(
    stateName: KokoAnimationStateName,
    previousVideo: HTMLVideoElement | null,
  ): void {
    this.visibleVideo = null;
    this.pendingVideo = null;
    this.currentAltId = null;
    this.autoPerchedAltId = null;
    this.perchedBaseLoopCount = 0;
    this.containerElement.style.opacity = "0";
    this.imageElement.style.visibility = "visible";
    this.imageElement.style.opacity = "1";
    if (previousVideo) {
      previousVideo.style.opacity = "0";
      previousVideo.style.visibility = "hidden";
      previousVideo.pause();
    }
    this.fallbackRenderer.setAnimation(stateName);
    this.resolvePresentable();
  }

  private handlePlaybackProgress(
    stateName: KokoAnimationStateName,
    altId: string | null,
    element: HTMLVideoElement,
  ): void {
    if (this.visibleVideo !== element) return;
    if (stateName !== this.currentState || altId !== this.currentAltId) return;

    const key = this.getVideoKey(stateName, altId);
    const record = this.videos.get(key);
    if (!record) return;

    const currentTime = element.currentTime;
    const lastTime = record.lastPlaybackTime;
    record.lastPlaybackTime = currentTime;
    if (currentTime >= lastTime - 0.05) return;

    this.handleLoopCompletion(stateName, altId);
  }

  private handleLoopCompletion(
    stateName: KokoAnimationStateName,
    altId: string | null,
  ): void {
    if (stateName !== "perched") return;

    if (altId === null) {
      const altIds = getBirdStateAltIds(this.variant, "perched");
      if (altIds.length === 0) return;
      this.perchedBaseLoopCount += 1;
      if (this.perchedBaseLoopCount < PERCHED_BASE_LOOPS_PER_ALT) return;

      this.perchedBaseLoopCount = 0;
      const nextAltId = altIds[this.perchedAltRotationIndex % altIds.length];
      this.perchedAltRotationIndex = (this.perchedAltRotationIndex + 1) % altIds.length;
      this.setAnimation("perched", nextAltId);
      this.autoPerchedAltId = nextAltId;
      return;
    }

    if (this.autoPerchedAltId !== altId) return;
    this.autoPerchedAltId = null;
    this.perchedBaseLoopCount = 0;
    this.setAnimation("perched");
  }

  private handleVideoError(
    stateName: KokoAnimationStateName,
    element: HTMLVideoElement,
  ): void {
    this.failedStates.add(stateName);
    element.pause();
    element.style.opacity = "0";
    element.style.visibility = "hidden";
    if (
      this.currentState !== stateName ||
      (this.pendingVideo !== element && this.visibleVideo !== element)
    ) {
      return;
    }

    this.activateFallback(stateName, this.visibleVideo);
    if (this.running) this.fallbackRenderer.start();
    else this.fallbackRenderer.stop();
  }

  private resolveAltId(
    stateName: KokoAnimationStateName,
    altId: string | null,
  ): string | null {
    const resolved = resolveBirdStateAsset(this.variant, stateName, altId);
    if (!resolved) return null;
    return resolved.altId;
  }

  private getVideoKey(
    stateName: KokoAnimationStateName,
    altId: string | null,
  ): string {
    return `${stateName}:${altId ?? ""}`;
  }

  private assetUrl(relativePath: string): string {
    return `${this.baseUrl.replace(/\/$/, "")}/${relativePath.replace(/^\/+/, "")}`;
  }

  whenReady(): Promise<void> {
    return this.presentableReadyPromise;
  }

  private resolvePresentable(): void {
    if (this.presentableReadyResolve) {
      this.presentableReadyResolve();
      this.presentableReadyResolve = null;
      this.presentableReadyPromise = Promise.resolve();
    }
  }
}

